import { FOLLOWERS_OR_FOLLOWING_PAGE_SIZE } from "@/constants/user"
import { FollowStatus, PublicUser, UserRef } from "@/types/user"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { isFollowingStatus } from "@/lib/user-utils"
import { UserApi } from "../user-api"
import { AppDispatch, RootState } from "./redux-store"
import { selectCurrentUserRef, userSlice } from "./user-slice"

const userApi = new UserApi(buildClientAxios())

export const fetchFollowersOrFollowing = createAsyncThunk<
  { users: PublicUser[]; hasMore: boolean },
  { userRef: UserRef; type: "followers" | "following" },
  { rejectValue: string }
>(
  "user/fetchFollowersOrFollowing",
  async ({ userRef, type }, { rejectWithValue }) => {
    try {
      if (type === "followers") {
        const response = await userApi.getFollowers(
          userRef,
          1,
          FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
        )
        return {
          users: response.follower_infos.map((follower) => follower.user),
          hasMore: !!response.meta.next_page,
        }
      } else {
        const response = await userApi.getFollowing(
          userRef,
          1,
          FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
        )
        return {
          users: response.followees,
          hasMore: !!response.meta.next_page,
        }
      }
    } catch (error) {
      return rejectWithValue("Failed to fetch followers/following")
    }
  }
)

export const loadMoreFollowersOrFollowing = createAsyncThunk<
  { users: PublicUser[]; hasMore: boolean },
  { userRef: UserRef; type: "followers" | "following"; page: number },
  { rejectValue: string }
>(
  "user/loadMoreFollowersOrFollowing",
  async ({ userRef, type, page }, { rejectWithValue }) => {
    try {
      if (type === "followers") {
        const response = await userApi.getFollowers(
          userRef,
          page,
          FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
        )
        return {
          users: response.follower_infos.map((follower) => follower.user),
          hasMore: !!response.meta.next_page,
        }
      } else {
        const response = await userApi.getFollowing(
          userRef,
          page,
          FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
        )
        return {
          users: response.followees,
          hasMore: !!response.meta.next_page,
        }
      }
    } catch (error) {
      return rejectWithValue("Failed to load more followers/following")
    }
  }
)

export const toggleFollow = createAsyncThunk<
  { success: boolean; followStatus: FollowStatus },
  { targetUserRef: UserRef; currentFollowStatus: FollowStatus },
  { state: RootState; rejectValue: string }
>(
  "user/toggleFollow",
  async (
    { targetUserRef, currentFollowStatus },
    { getState, dispatch, rejectWithValue }
  ) => {
    const currentUserRef = selectCurrentUserRef(getState())

    try {
      let isCurrentlyFollowing = isFollowingStatus(currentFollowStatus)
      let currentStatus = currentFollowStatus

      if (currentStatus === undefined) {
        const result = await userApi.isFollowing(targetUserRef)
        isCurrentlyFollowing = isFollowingStatus(result)
        currentStatus = result
      }

      let newIsCurrentlyFollowing = isCurrentlyFollowing
      let newFollowStatus = currentFollowStatus
      let didToggle = false

      if (isFollowingStatus(currentStatus)) {
        await userApi.unfollowUser(targetUserRef)
        newIsCurrentlyFollowing = false
        newFollowStatus = "notfollowing"
        didToggle = true
        sendAnalyticsEvent({
          name: "UserUnfollow",
          properties: { targetUserRef: targetUserRef },
        })
      } else {
        if (
          !currentStatus ||
          (currentStatus !== "requested" && currentStatus !== "denied")
        ) {
          newFollowStatus = await userApi.followUser(targetUserRef)
          newIsCurrentlyFollowing = true
          didToggle = true
          sendAnalyticsEvent({
            name: "UserFollow",
            properties: { targetUserRef: targetUserRef },
          })
        }
      }

      if (didToggle) {
        dispatch(
          userSlice.actions.updateFollowersCount({
            userRef: targetUserRef,
            increment: newIsCurrentlyFollowing,
          })
        )

        dispatch(
          userSlice.actions.updateFollowingCount({
            userRef: currentUserRef,
            increment: newIsCurrentlyFollowing,
          })
        )
      }

      dispatch(
        userSlice.actions.setFollowStatus({
          userRef: targetUserRef,
          followStatus: newFollowStatus,
        })
      )

      return { success: true, followStatus: newFollowStatus }
    } catch (error) {
      return rejectWithValue("Failed to toggle follow status")
    }
  }
)

export const refreshCurrentUserInfo = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; rejectValue: string }
>("user/refreshCurrentUserInfo", async (_, { dispatch, rejectWithValue }) => {
  try {
    const [user, permissions] = await Promise.all([
      userApi.fetchUser(),
      userApi.getPermissions(),
    ])

    dispatch(userSlice.actions.setUser(user))
    dispatch(userSlice.actions.setUserPermissions(permissions))
  } catch (error) {
    return rejectWithValue("Failed to refresh user info")
  }
})

export const updateCookiePreferences = createAsyncThunk<
  void,
  { functional: boolean; advertising: boolean },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  "user/updateCookiePreferences",
  async ({ functional, advertising }, { dispatch, rejectWithValue }) => {
    try {
      const response = await userApi.updateUserProfile(
        {
          functional_cookies_enabled: functional,
          advertising_cookies_enabled: advertising,
        },
        // this can be called when there is no user, so want to skip the redirect in this case
        { skipAuthHandling: true }
      )
      if (response.user_profile) {
        dispatch(userSlice.actions.setUserProfile(response.user_profile))
      }
    } catch (error) {
      return rejectWithValue("Failed to update cookie preferences")
    }
  }
)
